import { useEffect, useState } from "react"

/**
 * `useOnline` — subscribe to browser online/offline events.
 *
 * Returns the current `navigator.onLine` value, re-rendering when the
 * browser dispatches `online` / `offline`. Initialised from `navigator`
 * on first render; falls back to `true` when `navigator` is unavailable
 * (SSR / test environments that haven't polyfilled it).
 *
 * Used by:
 *   - `pages/Pending/Index.tsx` — logout affordance depends on network
 *   - `pages/Invitations/Index.tsx` — resend/revoke dialogs disable offline
 *   - `pages/Invitations/Accept.tsx` — banner above the form + submit
 *     button disabled when offline (UX Spec §2 S1)
 */
export function useOnline(): boolean {
  const [online, setOnline] = useState(() =>
    typeof navigator === "undefined" ? true : navigator.onLine,
  )
  useEffect(() => {
    function on() {
      setOnline(true)
    }
    function off() {
      setOnline(false)
    }
    window.addEventListener("online", on)
    window.addEventListener("offline", off)
    // OKS-443 — re-sync against navigator.onLine on the next tick to recover
    // from a stale `false` read taken mid-navigation (e.g. the post-OAuth
    // redirect on /onboarding/welcome) where the compensating `online` event
    // was missed. No-op in the normal case: setOnline with the same value
    // bails out of a re-render.
    const id = window.setTimeout(() => setOnline(navigator.onLine), 0)
    return () => {
      window.clearTimeout(id)
      window.removeEventListener("online", on)
      window.removeEventListener("offline", off)
    }
  }, [])
  return online
}
